import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/sidebar';
import { CreationCenter } from '@/pages/CreationCenter';
import { ProjectDetail } from '@/pages/ProjectDetail';
import { MaterialsPage } from '@/pages/Materials';
import { BatchManager } from '@/pages/BatchManager';
import { PlayableCenter } from '@/pages/PlayableCenter';
import { WorkflowPage } from '@/pages/Workflow';
import { PlayableManagement } from '@/pages/PlayableManagement';
import { ComponentGallery } from '@/pages/ComponentGallery';
import { PlayableDoctor } from '@/pages/PlayableDoctor';
import { LoginPage } from '@/pages/Login';
import { AuthCallbackPage } from '@/pages/AuthCallback';
import { isAuthenticated } from '@/utils/auth';

/**
 * 路由认证保护：
 * - 已有 token → 直接放行
 * - 无 token 且后端已配置飞书登录 → 跳转 /login
 * - 无 token 且后端未配置飞书（开发模式）→ 放行，不强制登录
 */
function RequireAuth({ children }: { children: JSX.Element }): JSX.Element | null {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      setChecked(true);
      return;
    }
    // 无 token：探测后端是否启用了飞书登录
    fetch('/api/auth/config')
      .then((r) => r.json())
      .then((data) => {
        if (data.feishuConfigured) {
          navigate('/login', { replace: true });
        } else {
          setChecked(true); // 开发模式未配置飞书：放行
        }
      })
      .catch(() => setChecked(true)); // 后端不可达：放行避免死循环
  }, [navigate]);

  if (!checked) return null;
  return children;
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  // Public — login & auth callback
  { path: '/login', element: <LoginPage /> },
  { path: '/auth/callback', element: <AuthCallbackPage /> },
  // Workflow — full screen
  {
    path: '/workflow/:sessionId',
    element: (
      <RequireAuth>
        <WorkflowPage />
      </RequireAuth>
    ),
  },
  // Project detail — full screen, no sidebar
  {
    path: '/creation/:projectId',
    element: (
      <RequireAuth>
        <ProjectDetail />
      </RequireAuth>
    ),
  },
  // Playable management — full screen
  {
    path: '/project/:projectId/playables',
    element: (
      <RequireAuth>
        <PlayableManagement />
      </RequireAuth>
    ),
  },
  // Main layout with sidebar
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/creation" replace /> },
      { path: 'creation', element: <CreationCenter /> },
      { path: 'playables', element: <PlayableCenter /> },
      { path: 'materials', element: <MaterialsPage /> },
      { path: 'batch', element: <BatchManager /> },
      { path: 'components', element: <ComponentGallery /> },
      { path: 'doctor', element: <PlayableDoctor /> },
    ],
  },
]);
